import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { AppState } from 'app/state/app.state';
import { firstValueFrom, map } from 'rxjs';
import { AccessLevel, ProductContextType } from '../models';

interface HubSpotConversationsApi {
  widget: {
    load(): void;
    remove(): void;
  };
  on(eventName: string, callback: () => void): void;
}

interface HubSpotWindow extends Window {
  HubSpotConversations?: HubSpotConversationsApi;
  hsConversationsOnReady?: Array<() => void>;
  hsConversationsSettings?: {
    loadImmediately: boolean;
    identificationEmail?: string;
    identificationToken?: string;
  };
}

interface VisitorIdentificationToken {
  email: string;
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class HubSpotService {
  private store = inject<Store<AppState>>(Store);
  private http = inject(HttpClient);
  private configurationService = inject(ConfigurationService);
  private loaded = false;
  private loadRequestId = 0;
  private onReady?: () => void;

  private allowedHubspotLevels: string[] = [
    AccessLevel.ExternalCustomerAdmin,
    AccessLevel.InternalCustomerAdmin,
    AccessLevel.PlatformOwner,
    AccessLevel.SuperAdmin,
    AccessLevel.SurveyAdmin,
    AccessLevel.Admin,
    AccessLevel.OrgAdmin,
  ];

  private isHandset = toSignal(this.store.select(s => s.navtreeState.isHandset));
  private productContextKey = toSignal(this.store.select(s => s.companyContext?.productContext?.key));
  private userInfo = toSignal(this.store.select(s => s.userInfo));
  private userHasHubSpotAccess = toSignal(this.store.select((s) => s.userInfo.userPermission).pipe(
    map((p) => p?.isAdmin || this.hubSpotAccess(p?.accessLevels)),
  ));
  private contextEffect = effect(() => {
    const key = this.productContextKey();
    const hasAccess = this.userHasHubSpotAccess();
    const userInfo = this.userInfo();
    if (key === ProductContextType.EX && hasAccess && userInfo?.userId) {
      void this.loadScript();
    } else {
      this.removeScript();
    }
  });

  private async loadScript(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    const requestId = ++this.loadRequestId;
    const visitorIdentification = await this.getVisitorIdentification();

    if (!this.loaded || requestId !== this.loadRequestId) return;

    const hubspotWindow = window as HubSpotWindow;
    hubspotWindow.hsConversationsSettings = {
      loadImmediately: false,
      ...(visitorIdentification && {
        identificationEmail: visitorIdentification.email,
        identificationToken: visitorIdentification.token,
      }),
    };

    this.onReady = () => {
      const conversations = hubspotWindow.HubSpotConversations;
      if (!conversations || !this.loaded || requestId !== this.loadRequestId) return;

      conversations.widget.load();
      conversations.on('widgetLoaded', () => {
        if (this.isHandset()) return;
        const container = document.getElementById('hubspot-messages-iframe-container');
        container?.style.setProperty('bottom', '72px', 'important');
        container?.style.setProperty('right', '0px', 'important');
      });
    };

    if (hubspotWindow.HubSpotConversations) {
      this.onReady();
      return;
    }

    hubspotWindow.hsConversationsOnReady ??= [];
    hubspotWindow.hsConversationsOnReady.push(this.onReady);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hs-script-loader';
    script.async = true;
    script.defer = true;
    script.src = '//js-eu1.hs-scripts.com/8416921.js';
    document.head.appendChild(script);
  }

  private removeScript(): void {
    if (!this.loaded) return;
    this.loadRequestId++;

    const hubspotWindow = window as HubSpotWindow;
    hubspotWindow.HubSpotConversations?.widget.remove();
    if (this.onReady && hubspotWindow.hsConversationsOnReady) {
      hubspotWindow.hsConversationsOnReady = hubspotWindow.hsConversationsOnReady.filter(
        callback => callback !== this.onReady,
      );
    }
    document.getElementById('hs-script-loader')?.remove();
    delete hubspotWindow.hsConversationsSettings;
    this.onReady = undefined;
    this.loaded = false;
  }

  private async getVisitorIdentification(): Promise<VisitorIdentificationToken | null> {
    if (!this.userInfo()?.email) return null;

    try {
      return await firstValueFrom(
        this.http.post<VisitorIdentificationToken>(
          `${this.configurationService.apiUrl}/api/hubspot/visitor-identification-token`,
          {},
        ),
      );
    } catch {
      return null;
    }
  }

  hubSpotAccess(accessLevels: string[]): boolean {
    return (
      accessLevels != null &&
      accessLevels.some((level) => this.allowedHubspotLevels.includes(level))
    );
  }
}


